import { useEffect, useMemo, useState, useCallback } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../stores/hooks/hooks.ts";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { faEnvelope, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { formatDateToMonthDayYear } from "../../../../../helpers/functions/formatDate.tsx";
import { CompanyMember } from "../../../../../stores/types/companyTypes.tsx";
import {
  fetchCompanyUsersThunk,
  deleteInvitationThunk,
  updateUserRoleThunk,
  resendInvitationThunk,
} from "../../../../../stores/thunks/companyThunk.tsx";
import {
  RevAvatar,
  RevCard,
  RevIconButton,
  RevSpinner,
  RevStatusBadge,
  RevTable,
  RevSelect,
} from "@revmatics/catalyst";
import { DeleteInvitationDialog } from "./DeleteInvitationDialog.tsx";
import { useParams } from "react-router";
import { roleOptions } from "./userData.ts";

const userColumnHelper = createColumnHelper<CompanyMember>();

// --- Helper function to format role strings ---
const formatRole = (role: string) => {
  if (!role) return "";
  const option = roleOptions.find((opt) => opt.value === role);
  return option ? option.label : role;
};

const RoleCell = ({
  user,
  isCurrentUserAdmin,
  currentUserEmail,
}: {
  user: CompanyMember;
  isCurrentUserAdmin?: boolean;
  currentUserEmail?: string;
}) => {
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const handleRoleChange = (newRole: string) => {
    dispatch(
      updateUserRoleThunk({
        id: user.id,
        role: newRole,
        itemType: user.user_is_active ? "member" : "invitation",
      }),
    );
  };

  const canEditRole =
    isCurrentUserAdmin &&
    currentUserEmail !== undefined &&
    user.email !== currentUserEmail &&
    !companyId;

  if (canEditRole) {
    return (
      <RevSelect
        name={`role-select-${user.id}`}
        options={roleOptions}
        value={user.role}
        onChange={(value) => {
          if (value && typeof value === "string") {
            handleRoleChange(value);
          }
        }}
      />
    );
  }

  return <span>{formatRole(user.role)}</span>;
};

type Props = {
  companyUsers?: CompanyMember[];
  isLoading?: boolean;
};

export const UsersView = ({ companyUsers, isLoading }: Props) => {
  const dispatch = useAppDispatch();
  const { companyId } = useParams();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [invitationToDelete, setInvitationToDelete] =
    useState<CompanyMember | null>(null);
  const { isDeletingInvitation, isResendingInvitation } = useAppSelector(
    (state) => state.company,
  );

  const {
    roles,
    profile,
    isLoading: isLoadingProfile,
  } = useAppSelector((state) => state.profile);
  const isUserAdmin = roles?.includes("ORGANIZATION_ADMIN");
  const isOnboardingUser = roles?.includes("INTERNAL_CUSTOMER_ONBOARDING");

  useEffect(() => {
    if (!companyUsers) {
      dispatch(fetchCompanyUsersThunk());
    }
  }, [companyUsers, dispatch]);

  const handleOpenDeleteDialog = useCallback((invitation: CompanyMember) => {
    setInvitationToDelete(invitation);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (!invitationToDelete) return;
    dispatch(deleteInvitationThunk(invitationToDelete.id))
      .unwrap()
      .finally(() => {
        setIsDeleteDialogOpen(false);
        setInvitationToDelete(null);
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<CompanyMember, any>[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseColumns: ColumnDef<CompanyMember, any>[] = [
      userColumnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
        id: "user",
        header: "User",
        cell: (info) => (
          <div className="flex items-center gap-3">
            <RevAvatar
              variant="circle"
              size="medium"
              theme="light"
              firstName={info.row.original.first_name}
              lastName={info.row.original.last_name}
            />
            <div>
              <p className="typography-body-sm text-monochrome-900">
                {info.getValue()}
              </p>
              <p className="typography-body-xs text-monochrome-500">
                {info.row.original.email}
              </p>
            </div>
          </div>
        ),
      }),
      userColumnHelper.accessor("created_at", {
        header: "Created on",
        cell: (info) => {
          const value = info.getValue();
          return value ? formatDateToMonthDayYear(value) : "–";
        },
      }),
      userColumnHelper.accessor("updated_at", {
        header: "Modified on",
        cell: (info) => {
          const value = info.getValue();
          return value ? formatDateToMonthDayYear(value) : "–";
        },
      }),
      userColumnHelper.display({
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const { user_is_active, invitation_email_sent } = row.original;
          const color = user_is_active
            ? "success"
            : invitation_email_sent
              ? "info"
              : "warning";
          const text = user_is_active
            ? "Active"
            : invitation_email_sent
              ? "Invited"
              : "Pending";
          return <RevStatusBadge color={color} text={text} />;
        },
      }),
      userColumnHelper.accessor("role", {
        header: "Role",
        cell: (info) => (
          <RoleCell
            user={info.row.original}
            isCurrentUserAdmin={isUserAdmin}
            currentUserEmail={profile?.email}
          />
        ),
      }),
    ];

    const adminActionColumn = userColumnHelper.display({
      id: "actions",
      header: () => <span></span>,
      cell: ({ row }) => {
        const item = row.original;
        if (!item.user_is_active) {
          return (
            <div className="flex items-center justify-center">
              <RevIconButton
                icon={faTrash}
                variant="default"
                size="s"
                iconSize="small"
                classNameColor="text-monochrome-500 hover:text-red-500"
                handleClick={() =>
                  handleOpenDeleteDialog(item as CompanyMember)
                }
                aria-label={`Delete invitation for ${item.email}`}
              />
            </div>
          );
        }
        return null; // No action for active members
      },
    });

    const sendEmailColumn = userColumnHelper.display({
      id: "sendEmail",
      header: "Invite",
      cell: ({ row }) => {
        const item = row.original;
        if (!item.user_is_active && item.invitation_id) {
          const label = item.invitation_email_sent
            ? `Resend invitation email to ${item.email}`
            : `Send invitation email to ${item.email}`;
          return (
            <div className="flex items-center justify-center" title={label}>
              <RevIconButton
                icon={faEnvelope}
                variant="default"
                size="s"
                iconSize="small"
                classNameColor="text-monochrome-500 hover:text-primary-500"
                handleClick={() =>
                  dispatch(
                    resendInvitationThunk({
                      companyId: Number(companyId),
                      invitationId: item.invitation_id!,
                    }),
                  )
                }
                disabled={isResendingInvitation}
                aria-label={label}
              />
            </div>
          );
        }
        return null;
      },
    });

    const showSendEmail = isOnboardingUser && !!companyId;
    return [
      ...baseColumns,
      ...(showSendEmail ? [sendEmailColumn] : []),
      ...(isUserAdmin ? [adminActionColumn] : []),
    ];
  }, [
    isUserAdmin,
    isOnboardingUser,
    companyId,
    isResendingInvitation,
    profile?.email,
    dispatch,
    handleOpenDeleteDialog,
  ]);

  if (isLoadingProfile || isLoading) {
    return (
      <div className="h-full">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  return (
    <>
      <RevCard padding="p-0">
        <RevTable<CompanyMember>
          data={companyUsers || []}
          columns={columns}
          initialPageSize={10}
          enableGlobalFilter={false}
          enablePagination={true}
        />
      </RevCard>
      {isUserAdmin && (
        <DeleteInvitationDialog
          isOpen={isDeleteDialogOpen}
          setIsOpen={setIsDeleteDialogOpen}
          invitation={invitationToDelete}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeletingInvitation}
        />
      )}
    </>
  );
};
